import { __awaiter } from '../_virtual/_tslib.js';
import { getInitialContextValues } from '../methods/getInitialContextValues.js';
import { getUniqueArray } from '../methods/getUniqueArray.js';
import { useState, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { COOKIES, DefaultCookieOptions } from '../constants/misc.js';
import { useRouter } from 'next/router';
import { useGetCurrentLocationOnPageLoad } from './useGetCurrentLocation.js';
import { LocationSearchSource } from '../_services/city-search.service.js';
import useMediaQuery from './useMediaQuery.js';

function useSetAppContext({ src } = {}) {
    const ENV = process.env.REACT_APP_ENV || process.env.NEXT_PUBLIC_REACT_APP_ENV;
    const isProductionEnv = ENV === "production";
    const router = useRouter();
    const [cookies, setCookie, removeCookie] = useCookies([]);
    const isMobileScreen = useMediaQuery({ queryType: "mobile" });
    const [context, setContext] = useState(Object.assign(Object.assign(Object.assign({}, getInitialContextValues()), (src === "csDashboard" && {
        isCsDashboardUser: true,
        csDashboardUserRoles: cookies.csdsbUserRoles,
        csDashboardPermission: cookies[COOKIES.csDashboardPermission]
    })), { isProductionEnv }));
    const currentCity = useGetCurrentLocationOnPageLoad({
        enabled: src !== "csDashboard",
        locationSearchParams: {
            source: LocationSearchSource.CompanyCurrentLocation
        }
    });
    const updateEventCount = (name, value) => {
        var _a, _b;
        const { eventCounts } = context;
        const newValue = Number((_b = value !== null && value !== void 0 ? value : (_a = context === null || context === void 0 ? void 0 : context.eventCounts) === null || _a === void 0 ? void 0 : _a[name]) !== null && _b !== void 0 ? _b : 0) + 1;
        if ((eventCounts === null || eventCounts === void 0 ? void 0 : eventCounts[name]) !== undefined) {
            eventCounts[name] = newValue;
        }
        setCookie(COOKIES[name], newValue);
        setContext((state) => (Object.assign(Object.assign({}, state), { eventCounts })));
    };
    const isUserLoggedIn = !!(context.customerCompanyId || context.companyId);
    const checkRestrictedEvent = (name) => {
        var _a;
        switch (name) {
            case "citySearchCount":
            case "transporterSearchCount": {
                const count = Number(((_a = context === null || context === void 0 ? void 0 : context.eventCounts) === null || _a === void 0 ? void 0 : _a[name]) || 0);
                return count >= 3;
            }
            case "pagination": {
                return true;
            }
            default: {
                return false;
            }
        }
    };
    const checkAndHandleGuestUserRestriction = (name) => {
        if (!isUserLoggedIn) {
            if (checkRestrictedEvent(name)) {
                router === null || router === void 0 ? void 0 : router.push({ hash: "#Login" });
                return false;
            }
            updateEventCount === null || updateEventCount === void 0 ? void 0 : updateEventCount(name);
        }
        return true;
    };
    useEffect(() => {
        setContext((context) => (Object.assign(Object.assign({}, context), { isMobileScreen })));
    }, [isMobileScreen]);
    useEffect(() => {
        const fetchCurrentLocation = () => __awaiter(this, void 0, void 0, function* () {
            if (currentCity === null || currentCity === void 0 ? void 0 : currentCity.place_id) {
                setContext((context) => (Object.assign(Object.assign({}, context), { globalData: Object.assign(Object.assign({}, context.globalData), { userPlaceInfo: currentCity }) })));
            }
        });
        fetchCurrentLocation();
    }, [currentCity]);
    useEffect(() => {
        setContext((state) => (Object.assign(Object.assign(Object.assign(Object.assign({}, state), { isMobile: window.matchMedia("(max-width: 991px)").matches, updateBulkRequestIds: ({ action, fileName, requestId }) => {
                const prevReqIds = state.bulkRequestIds;
                switch (action) {
                    case "ADD":
                        const addedReqIds = [...prevReqIds];
                        // Checking whether the particular id is already present
                        if (!addedReqIds.find((reqId) => reqId.requestId === requestId)) {
                            addedReqIds.push({ requestId, fileName });
                            setCookie(COOKIES.bulkRequestIdArray, JSON.stringify(addedReqIds), DefaultCookieOptions);
                            setContext((state) => (Object.assign(Object.assign({}, state), { bulkRequestIds: addedReqIds })));
                        }
                        break;
                    case "REMOVE":
                        const updatedReqIds = prevReqIds.filter((reqIdObj) => reqIdObj.requestId !== requestId);
                        setCookie(COOKIES.bulkRequestIdArray, JSON.stringify(updatedReqIds), DefaultCookieOptions);
                        setContext((state) => (Object.assign(Object.assign({}, state), { bulkRequestIds: updatedReqIds })));
                        break;
                }
            }, openBulkModal: () => {
                setContext((state) => (Object.assign(Object.assign({}, state), { isBulkUploadModalOpen: true })));
            }, closeBulkModal: () => {
                setContext((state) => (Object.assign(Object.assign({}, state), { isBulkUploadModalOpen: false })));
            }, updateHeaderClassName: (className) => {
                setContext((state) => (Object.assign(Object.assign({}, state), { headerClassName: className })));
            }, setCurrentLocation: (loc) => setContext((state) => (Object.assign(Object.assign({}, state), { currentLocation: loc }))), recentTransporterCitySearches: cookies.rtcs, updateRecentTransporterCitySearches: (city) => {
                city &&
                    setContext((state) => {
                        const updatedRecents = getUniqueArray([
                            Object.assign(Object.assign({}, city), { category: "recent", categoryLabel: "RECENT SEARCHES" }),
                            ...(state.recentTransporterCitySearches || [])
                        ], "id").slice(0, 5);
                        setCookie(COOKIES.recentTransporterCitySearches, JSON.stringify(updatedRecents), DefaultCookieOptions);
                        return Object.assign(Object.assign({}, state), { recentTransporterCitySearches: updatedRecents });
                    });
            }, setPhoneNum: (phoneNum) => {
                if (phoneNum) {
                    setCookie("phoneNum", phoneNum, DefaultCookieOptions);
                    setContext((state) => (Object.assign(Object.assign({}, state), { phoneNum })));
                }
            }, setName: (name) => {
                if (name) {
                    setCookie("name", name, DefaultCookieOptions);
                }
                else {
                    removeCookie("name", DefaultCookieOptions);
                }
                setContext((state) => (Object.assign(Object.assign({}, state), { name })));
            }, setUserId: (userId) => {
                if (userId) {
                    setCookie(COOKIES.userId, userId, DefaultCookieOptions);
                }
                else {
                    removeCookie(COOKIES.userId, DefaultCookieOptions);
                }
                setContext((state) => (Object.assign(Object.assign({}, state), { userId: userId })));
            }, setCompanyId: (companyId) => {
                if (companyId) {
                    setCookie(COOKIES.companyId, companyId, DefaultCookieOptions);
                }
                else {
                    removeCookie(COOKIES.companyId, DefaultCookieOptions);
                }
                setContext((state) => (Object.assign(Object.assign({}, state), { companyId })));
            }, setCompanyLocation: (companyLocation) => {
                if (companyLocation) {
                    setCookie(COOKIES.companyLocation, companyLocation, DefaultCookieOptions);
                }
                else {
                    removeCookie(COOKIES.companyLocation, DefaultCookieOptions);
                }
                setContext((state) => (Object.assign(Object.assign({}, state), { companyLocation })));
            }, setCompanyLogo: (logo) => {
                if (logo) {
                    setCookie(COOKIES.companyLogo, logo, DefaultCookieOptions);
                }
                else {
                    removeCookie(COOKIES.companyLogo, DefaultCookieOptions);
                }
                setContext((state) => (Object.assign(Object.assign({}, state), { companyLogo: logo })));
            }, setIsPhoneVerified: (isVerified) => {
                if (isVerified) {
                    setCookie("vv", 1, Object.assign({}, DefaultCookieOptions));
                }
                else {
                    removeCookie("vv", DefaultCookieOptions);
                }
                setContext((state) => (Object.assign(Object.assign({}, state), { isPhoneVerified: isVerified })));
            }, isEmailVerified: !!+cookies.ev, setIsEmailVerified: (isEmailVerified) => {
                if (isEmailVerified) {
                    setCookie(COOKIES.isEmailVerified, 1, Object.assign({}, DefaultCookieOptions
                    // maxAge: ENV === "production" ? 30 * 60 : 5 * 60
                    ));
                }
                else {
                    removeCookie(COOKIES.isEmailVerified, DefaultCookieOptions);
                }
                setContext((state) => (Object.assign(Object.assign({}, state), { isEmailVerified: isEmailVerified })));
            }, isRecoverEmailVerified: !!+cookies.rev, setIsRecoverEmailVerified: (isRecoveryEmailVerified) => {
                if (isRecoveryEmailVerified) {
                    setCookie(COOKIES.isRecoveryEmailVerified, 1, Object.assign({}, DefaultCookieOptions
                    // maxAge: ENV === "production" ? 30 * 60 : 5 * 60
                    ));
                }
                else {
                    removeCookie(COOKIES.isRecoveryEmailVerified, DefaultCookieOptions);
                }
                setContext((state) => (Object.assign(Object.assign({}, state), { isRecoverEmailVerified: isRecoveryEmailVerified })));
            } }), (src === "csDashboard" &&
            {
                setCsDashboardUserRoles: (csDashboardUserRoles) => {
                    if (csDashboardUserRoles) {
                        setCookie(COOKIES.csDashboardUserRoles, csDashboardUserRoles, Object.assign({}, DefaultCookieOptions
                        // maxAge: ENV === "production" ? 30 * 60 : 5 * 60
                        ));
                    }
                    else {
                        removeCookie(COOKIES.csDashboardUserRoles, DefaultCookieOptions);
                    }
                    setContext((state) => (Object.assign(Object.assign({}, state), { csDashboardUserRoles: csDashboardUserRoles })));
                },
                setCsDashboardPermission: (csDashboardPermission) => {
                    if (csDashboardPermission) {
                        setCookie(COOKIES.csDashboardPermission, csDashboardPermission, DefaultCookieOptions);
                    }
                    else {
                        removeCookie(COOKIES.csDashboardPermission, DefaultCookieOptions);
                    }
                    setContext((state) => (Object.assign(Object.assign({}, state), { csDashboardPermission })));
                },
                customerCompanyId: cookies.ccid,
                setCustomerCompanyId: (customerCompanyId) => {
                    if (customerCompanyId) {
                        setCookie(COOKIES.customerCompanyId, customerCompanyId, Object.assign({}, DefaultCookieOptions
                        // maxAge: ENV === "production" ? 30 * 60 : 5 * 60
                        ));
                    }
                    else {
                        removeCookie(COOKIES.customerCompanyId, DefaultCookieOptions);
                    }
                    setContext((state) => (Object.assign(Object.assign({}, state), { customerCompanyId: customerCompanyId })));
                },
                customerCompanyLocation: cookies.ccloc,
                setCustomerCompanyLocation: (customerCompanyLocation) => {
                    if (customerCompanyLocation) {
                        setCookie(COOKIES.customerCompanyLocation, customerCompanyLocation, Object.assign({}, DefaultCookieOptions
                        // maxAge: ENV === "production" ? 30 * 60 : 5 * 60
                        ));
                    }
                    else {
                        removeCookie(COOKIES.customerCompanyLocation, DefaultCookieOptions);
                    }
                    setContext((state) => (Object.assign(Object.assign({}, state), { customerCompanyLocation: customerCompanyLocation })));
                },
                customerPhone: cookies.cpn,
                setCustomerPhone: (customerPhone) => {
                    if (customerPhone) {
                        setCookie(COOKIES.customerPhone, customerPhone, Object.assign({}, DefaultCookieOptions
                        // maxAge: ENV === "production" ? 30 * 60 : 5 * 60
                        ));
                    }
                    else {
                        removeCookie(COOKIES.customerPhone, DefaultCookieOptions);
                    }
                    setContext((state) => (Object.assign(Object.assign({}, state), { customerPhone: customerPhone })));
                },
                customerName: cookies.cname,
                setCustomerName: (customerName) => {
                    if (customerName) {
                        setCookie(COOKIES.customerName, customerName, Object.assign({}, DefaultCookieOptions
                        // maxAge: ENV === "production" ? 30 * 60 : 5 * 60
                        ));
                    }
                    else {
                        removeCookie(COOKIES.customerName, DefaultCookieOptions);
                    }
                    setContext((state) => (Object.assign(Object.assign({}, state), { customerName: customerName })));
                },
                customerServiceType: cookies.cst,
                setCustomerServiceType: (customerServiceType) => {
                    if (customerServiceType) {
                        setCookie(COOKIES.customerServiceType, customerServiceType, Object.assign({}, DefaultCookieOptions
                        // maxAge: ENV === "production" ? 30 * 60 : 5 * 60
                        ));
                    }
                    else {
                        removeCookie(COOKIES.customerServiceType, DefaultCookieOptions);
                    }
                    setContext((state) => (Object.assign(Object.assign({}, state), { customerServiceType: customerServiceType })));
                },
                tsoLorrySpecialization: undefined,
                setTsoLorrySpecialization: (tsoLorrySpecialization) => {
                    setContext((state) => (Object.assign(Object.assign({}, state), { tsoLorrySpecialization })));
                }
            })), { numOfContactRqsts: +cookies.numOfContactRqsts, updateNumOfContactRqsts: () => {
                setContext((state) => {
                    const newNum = (state.numOfContactRqsts || 0) + 1;
                    const d = new Date();
                    setCookie("numOfContactRqsts", newNum, Object.assign(Object.assign({}, DefaultCookieOptions), { expires: ENV === "production"
                            ? new Date(new Date(d.setDate(d.getDate() + 1)).setHours(0))
                            : undefined, maxAge: ENV !== "production" ? 60 : undefined }));
                    return Object.assign(Object.assign({}, state), { numOfContactRqsts: newNum });
                });
            }, updateTransporterContactsRqstd: (newItem) => {
                setContext((state) => {
                    const newArr = state.transporterContactsRqstd
                        ? [...state.transporterContactsRqstd, newItem]
                        : [newItem];
                    const d = new Date();
                    setCookie("tcr", JSON.stringify(newArr), Object.assign(Object.assign({}, DefaultCookieOptions), { expires: ENV === "production"
                            ? new Date(new Date(d.setDate(d.getDate() + 1)).setHours(0))
                            : undefined, maxAge: ENV !== "production" ? 60 : undefined }));
                    return Object.assign(Object.assign({}, state), { transporterContactsRqstd: newArr });
                });
            }, updateProfilesViewed: (newProfileId) => {
                setContext((state) => {
                    const newArr = state.profilesViewed ? [...state.profilesViewed, newProfileId] : [newProfileId];
                    setCookie("pv", JSON.stringify(newArr), Object.assign({}, DefaultCookieOptions));
                    return Object.assign(Object.assign({}, state), { profilesViewed: newArr });
                });
            }, serviceType: cookies.st || undefined, setServiceType: (serviceType) => {
                if (serviceType) {
                    setCookie(COOKIES.serviceType, serviceType, DefaultCookieOptions);
                }
                else {
                    removeCookie(COOKIES.serviceType, DefaultCookieOptions);
                }
                setContext((state) => (Object.assign(Object.assign({}, state), { serviceType: serviceType })));
            }, setKycPopupInitialLoad: () => {
                setContext((state) => {
                    let temp;
                    if (Number(state.kycPopupInitialLoad) < 2) {
                        temp = Number(state.kycPopupInitialLoad) + 1;
                        setCookie(COOKIES.kycPopupInitialLoad, temp, Object.assign(Object.assign({}, DefaultCookieOptions), { expires: undefined, maxAge: undefined }));
                    }
                    else if (Number(state.kycPopupInitialLoad) === 2) {
                        setCookie(COOKIES.kycPopupInitialLoad, Number(state.kycPopupInitialLoad) + 1, Object.assign(Object.assign({}, DefaultCookieOptions), { expires: new Date(Date.now() + 24 * 60 * 60 * 5 * 1000), maxAge: ENV !== "production" ? 60 : undefined }));
                    }
                    return Object.assign(Object.assign({}, state), { kycPopupInitialLoad: temp });
                });
            }, setHistoryLocation: (historyLocation) => {
                setContext((state) => {
                    setCookie(COOKIES.historyLocation, historyLocation, Object.assign(Object.assign({}, DefaultCookieOptions), { expires: undefined, maxAge: undefined }));
                    return Object.assign(Object.assign({}, state), { historyLocation: historyLocation });
                });
            }, shouldPreventLayoutSwitch: context.shouldPreventLayoutSwitch, setShouldPreventLayoutSwitch: (shouldPreventLayoutSwitch) => {
                setContext((state) => (Object.assign(Object.assign({}, state), { shouldPreventLayoutSwitch: shouldPreventLayoutSwitch })));
            }, fromFlutterApp: cookies.fromFlutterApp || undefined, setUserAppVersion: (version) => {
                setContext((state) => (Object.assign(Object.assign({}, state), { userAppVersion: version })));
            }, setShowAppRedirectionPopup: (val, props) => {
                setContext((state) => (Object.assign(Object.assign({}, state), { showAppRedirectionPopup: val, appRedirectionPopupProps: val ? props : undefined })));
            }, setGlobalData: (data) => {
                setContext((state) => (Object.assign(Object.assign({}, state), { globalData: Object.assign(Object.assign({}, state === null || state === void 0 ? void 0 : state.globalData), data) })));
            }, updateEventCount,
            checkAndHandleGuestUserRestriction, referralCode: cookies.rfc || undefined, setReferralCode: (referralCode) => {
                const result = new Date();
                result.setDate(result.getDate() + 13); // 14 days expiry time
                if (referralCode) {
                    setCookie(COOKIES.referralCode, referralCode, Object.assign(Object.assign({}, DefaultCookieOptions), { expires: result }));
                }
                else {
                    removeCookie(COOKIES.referralCode, DefaultCookieOptions);
                }
                setContext((state) => (Object.assign(Object.assign({}, state), { referralCode: referralCode })));
            }, updateRoute: (route) => {
                setContext((state) => (Object.assign(Object.assign({}, state), { route })));
            }, activateAppMandatePopup: () => {
                setContext((state) => (Object.assign(Object.assign({}, state), { showAppMandatePopup: true })));
            }, sessionId: +cookies[COOKIES.sessionId] || 0, setSessionId: (sessionId) => {
                sessionId
                    ? setCookie(COOKIES.sessionId, sessionId, DefaultCookieOptions)
                    : removeCookie(COOKIES.sessionId, DefaultCookieOptions);
                setContext((state) => (Object.assign(Object.assign({}, state), { sessionId })));
            }, serviceRequestType: context.serviceRequestType, setServiceRequestType: (serviceRequestType) => {
                setContext((state) => (Object.assign(Object.assign({}, state), { serviceRequestType })));
            }, setThirdPartyCompanyName: (companyName) => {
                if (companyName) {
                    setCookie(COOKIES.thirdPartyCompanyName, companyName, Object.assign({}, DefaultCookieOptions));
                }
                else {
                    removeCookie(COOKIES.thirdPartyCompanyName, DefaultCookieOptions);
                }
                setContext((state) => (Object.assign(Object.assign({}, state), { thirdPartyCompanyName: companyName })));
            }, isLorryNotAllowedPopupOpen: false, toggleIsLorryNotAllowedPopupOpen: () => {
                setContext((state) => (Object.assign(Object.assign({}, state), { isLorryNotAllowedPopupOpen: !state.isLorryNotAllowedPopupOpen })));
            } })));
        // if (window["wvp"]) {
        //     setLoginInfoInWebView();
        // }
        // getKycMethodTypes(KYC_VERIFICATION_METHODS_TYPE_URL);
    }, []);
    return { context, setContext };
}

export { useSetAppContext };
//# sourceMappingURL=useSetAppContext.js.map
